/**
 * Growth Ladder — the visible product ladder.
 *
 * Users see the 4-step journey from trainee PDI → solo ADI → coach/educator →
 * driving school. Two tiers are selectable today; the other two open a
 * "coming soon" waitlist. Copy is warm, progress-led, never salesy.
 *
 * Why this page matters: the biggest retention lever we have is making
 * users see the platform as their natural next step — not a product they'll
 * outgrow.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Check, GraduationCap, Compass, Users, Briefcase,
  Sparkles, Clock3, Bell,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = {
  trainee_pdi: GraduationCap,
  solo_adi: Compass,
  coach_educator: Users,
  driving_school: Briefcase,
};

const ACCENT_CLASSES = {
  violet: {
    bgSoft: "bg-violet-50", bgSolid: "bg-violet-600 hover:bg-violet-700",
    text: "text-violet-700", border: "border-violet-200",
    iconBg: "bg-violet-100 text-violet-700",
  },
  indigo: {
    bgSoft: "bg-indigo-50", bgSolid: "bg-indigo-600 hover:bg-indigo-700",
    text: "text-indigo-700", border: "border-indigo-200",
    iconBg: "bg-indigo-100 text-indigo-700",
  },
  emerald: {
    bgSoft: "bg-emerald-50", bgSolid: "bg-emerald-600 hover:bg-emerald-700",
    text: "text-emerald-700", border: "border-emerald-200",
    iconBg: "bg-emerald-100 text-emerald-700",
  },
  rose: {
    bgSoft: "bg-rose-50", bgSolid: "bg-rose-600 hover:bg-rose-700",
    text: "text-rose-700", border: "border-rose-200",
    iconBg: "bg-rose-100 text-rose-700",
  },
};

function TierCard({ tier, isCurrent, isWaitlisted, onSelect, onWaitlist, onLeaveWaitlist }) {
  const Icon = TIER_ICONS[tier.id] || Sparkles;
  const accent = ACCENT_CLASSES[tier.accent] || ACCENT_CLASSES.indigo;

  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className={`rounded-2xl border p-5 transition-colors ${
        isCurrent
          ? `${accent.bgSoft} ${accent.border} ring-2 ring-offset-2 ring-slate-900/5`
          : "bg-white border-slate-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-slate-900">{tier.name}</h3>
            {isCurrent && (
              <span
                data-testid={`tier-current-${tier.id}`}
                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${accent.bgSoft} ${accent.text}`}
              >
                <Check className="w-3 h-3" />
                You're here
              </span>
            )}
            {!tier.available && (
              <span
                data-testid={`tier-coming-soon-${tier.id}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded-full px-2 py-0.5"
              >
                <Clock3 className="w-3 h-3" />
                Coming soon
              </span>
            )}
            {isWaitlisted && (
              <span
                data-testid={`tier-waitlisted-${tier.id}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"
              >
                <Bell className="w-3 h-3" />
                On waitlist
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{tier.tagline}</p>

          {!tier.available && tier.coming_soon_highlights && (
            <ul className="mt-3 space-y-1">
              {tier.coming_soon_highlights.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-1.5 text-[11px] text-slate-500"
                >
                  <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
                  {h}
                </li>
              ))}
            </ul>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4">
            {tier.available && !isCurrent && (
              <button
                type="button"
                onClick={() => onSelect(tier)}
                data-testid={`tier-select-${tier.id}`}
                className={`inline-flex items-center gap-1.5 ${accent.bgSolid} text-white text-xs font-bold rounded-lg px-3 py-2 transition-colors`}
              >
                Switch to {tier.name}
              </button>
            )}
            {!tier.available && !isWaitlisted && (
              <button
                type="button"
                onClick={() => onWaitlist(tier)}
                data-testid={`tier-waitlist-join-${tier.id}`}
                className={`inline-flex items-center gap-1.5 ${accent.bgSolid} text-white text-xs font-bold rounded-lg px-3 py-2 transition-colors`}
              >
                <Bell className="w-3.5 h-3.5" />
                Join the waitlist
              </button>
            )}
            {!tier.available && isWaitlisted && (
              <button
                type="button"
                onClick={() => onLeaveWaitlist(tier)}
                data-testid={`tier-waitlist-leave-${tier.id}`}
                className="text-xs font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-2"
              >
                Leave waitlist
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GrowthLadder() {
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/account/tier`,
        { withCredentials: true },
      );
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectTier = async (tier) => {
    try {
      await axios.post(
        `${API}/api/account/tier`,
        { tier: tier.id },
        { withCredentials: true },
      );
      toast.success(`You're now on ${tier.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't switch");
    }
  };

  const joinWaitlist = async (tier) => {
    try {
      await axios.post(
        `${API}/api/account/tier-waitlist`,
        { tier: tier.id },
        { withCredentials: true },
      );
      toast.success(`You're on the ${tier.name} waitlist — we'll email you when it opens`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join waitlist");
    }
  };

  const leaveWaitlist = async (tier) => {
    try {
      await axios.delete(
        `${API}/api/account/tier-waitlist/${tier.id}`,
        { withCredentials: true },
      );
      toast.success(`Left the ${tier.name} waitlist`);
      load();
    } catch {
      toast.error("Couldn't update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }
  if (!data) return null;
  const { current_tier, waitlisted_tiers, ladder } = data;

  return (
    <div data-testid="growth-ladder-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="growth-ladder-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Your growth ladder</h1>
            <p className="text-[11px] text-slate-500">
              Every stage of your teaching career — one platform
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-5 text-white">
          <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-100">
            Built for every stage
          </p>
          <h2 className="text-lg font-bold mt-0.5">
            From Trainee PDI to your own Driving School — your platform grows with you
          </h2>
          <p className="text-sm text-indigo-100 mt-1 leading-relaxed">
            Two stages are live today. The other two are on the way — join a waitlist
            and we'll open the doors the moment they're ready.
          </p>
        </div>

        <div className="space-y-3">
          {(ladder || []).map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              isCurrent={current_tier === tier.id}
              isWaitlisted={(waitlisted_tiers || []).includes(tier.id)}
              onSelect={selectTier}
              onWaitlist={joinWaitlist}
              onLeaveWaitlist={leaveWaitlist}
            />
          ))}
        </div>

        <p className="text-[11px] text-slate-400 text-center leading-relaxed">
          Currently on: <span className="font-semibold text-slate-600">
            {current_tier ? (ladder.find((t) => t.id === current_tier)?.name || current_tier) : "Not set"}
          </span>
          . Change tiers any time — your pupils, calendar and progress all travel with you.
        </p>
      </div>
    </div>
  );
}
